import { updateQuotesAcrossTables } from "@/store/tradeDataStoreACTIONS";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  convertToDollarFraction,
  formatDollarFractionString,
  regexCheckFractionQuote,
  ruleOfEights,
  updateFieldOnParamsToQuote,
} from "../../../../../lib/businessRules/dollarFractionBusinessRule";
import {
  checkQuoteNumberSize,
  truncateValueIfNeeded,
} from "./DynamicCellEditorQuoteDollarFractionHelper";
import {
  isNumericallyZero,
  normalizeZeroQuote,
} from "@/lib/businessRules/zeroNormalizationHelper";
import { StrategyType } from "@/lib/interfaces and Enums/ENUMStrategyType";
const DynamicCellEditorQuoteDollarFraction = forwardRef((props: any, ref) => {
  const isLegLevel = props.colDef.cellRendererParams.tableLevel == "Leg";
  //Determine the field that is getting edited:
  const field = props.colDef.field;
  //
  const fullTradeData = props.colDef.cellRendererParams.fullTradeData;
  const isOutright = fullTradeData?.strategyType == StrategyType.Outright;
  // Check if the ER or Leg level is rendering the function and alter where to look for notation as a result.
  // Outrights at ER level must read from legs[0] because the ER-level notationER can drift from the true block notation.
  const notation = isLegLevel
    ? props.data.notation
    : isOutright
      ? (props.data.legs?.[0]?.notation ?? props.data.notationER)
      : props.data.notationER;
  const isUsingDollarFractionLogic = notation == "$fr";
  const typeValue = isUsingDollarFractionLogic ? "text" : "number";

  // Round the value of the first load.
  // NOTE: The Cube keeps the full length of a number in the zustand store. If a user does not interact with the number, it will always be used in full to send back to the API.
  // The moment a user interacts with the Editor - rounding is applied and the user will never see the full number. Added initialValueRounded so this occurs on click into the cell rather than only showing the rounding once a user clicks out.
  // const initalValueRounded = truncateValueIfNeeded(
  //   props.value,
  //   notation,
  // ).replace(/,/g, "");
  const [value, setValue] = useState(props.value?.toString() || "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // CHECK all places where tickSize gets updated in the code. DynamicCellEditorQuoteDollarFraction, formatDollarFractionZValueForSmallFractionDisplay
  const tickSize = ruleOfEights;

  // 15063 - noLonger using the logic of the block tick size and using the rule of 8s as a default.
  // if (isLegLevel) {
  //   if (props.data.tickSize != null) {
  //     tickSize = props.data.tickSize;
  //   }
  // } else {
  //   if (props.data.legs[0].tickSize != null) {
  //     tickSize = props.data.legs[0].tickSize;
  //   }
  // }

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;

    //First early return
    // Enforce rounding in real time based on decimal place values:
    // let shouldDecimalRoundingReturnEarly = enforceRoundingOnDecimalValues(
    //   event,
    //   input,
    //   notation,
    // );
    // if (shouldDecimalRoundingReturnEarly) {
    //   return;
    // }

    // Second early return = Numebr is too small or large
    //check max input value for non- $fr inputs - This will prevent numbers getting pasted into cells.
    const maxQuoteValue = !isLegLevel && notation === "$" ? 99999999999.99 : 9999999;
    const isNumberTooLargeOrSmall = checkQuoteNumberSize(event, input, maxQuoteValue);
    if (isNumberTooLargeOrSmall) {
      return;
    }
    const inputEvent = event.nativeEvent as InputEvent;

    // Third early return for decimals
    if (!isUsingDollarFractionLogic) {
      if (input == "") {
        if (inputEvent.inputType != "deleteContentBackward") {
          return;
        }
      }
      setValue(input);
      return;
    }

    // Allow only valid characters (`0-9`, `.`, `-`, `+`)
    if (!/^[\d.\-+]*$/.test(input)) {
      return; // Block invalid characters immediately
    }

    // Prevent multiple `.` (only one allowed)
    if ((input.match(/\./g) || []).length > 1) {
      return; // Block invalid characters immediately
    }

    // Prevent multiple `-` (two MAX allowed if 1 is used for a negative value.)
    if (
      (input.substring(1).match(/-/g) || []).length > 1 // Prevent 3 or more `-`
      // Allow max 2 `-`
      //!/^-\d+(-\d+)?$/.test(input) // Must match `-number` or `-number-number`
    ) {
      return; // Block invalid multiple `-`
    }

    // Prevent `.` and `-` from coexisting
    if (input.includes(".") && input.includes("-")) {
      return; // Block invalid characters immediately
    }

    // Prevent multiple `+` (only one allowed, must be at the end)
    if (
      (input.match(/\+/g) || []).length > 1 ||
      (input.includes("+") && !input.endsWith("+"))
    ) {
      return; // Block invalid characters immediately
    }

    // Prevent `+` if there is no `-` before it
    if (input.includes("+") && !input.includes("-")) {
      return; // Block invalid characters immediately
    }

    // Ensure at least one digit exists between `-` and `+`
    if (input.includes("-") && input.includes("+")) {
      const match = input.match(/-(\d{2,})\+/);
      if (!match || match[1].length === 0) {
        return; // Reject if `-+` appears without numbers in between
      }
    }

    /// --- JONTY SLACK MESSAGE: The two numbers after the hyphen in a fractional quote cannot be >31, if a user inputs a number greater than 31 in the two digits after the hyphen of a fractional quote, please either force it to be "31" or "00" and show a warning msg ///
    // START no number above 31 after the fraction //
    // Prevent more than 3 digits after `-`
    const dashMatch = input.substring(1).match(/-(\d*)/);
    //
    if (dashMatch && dashMatch[1].length == 1) {
      if (Number(dashMatch[1]) > 3) {
        return;
      }
    }

    if (dashMatch && dashMatch[1].length == 2) {
      // Number can only be 31 or smaller after the
      if (Number(dashMatch[1]) > 31) {
        return;
      }
    }
    // END no number above 31 after the fraction //

    if (dashMatch && dashMatch[1].length > 2) {
      if (tickSize == ruleOfEights) {
        if (
          dashMatch[1][2] == "0" ||
          dashMatch[1][2] == "8" ||
          dashMatch[1][2] == "9"
        ) {
          return; // Block invalid character - Y != 8 or 9
        }
      }
      //   if (tickSize == ruleOfQuarters) {
      //     if (
      //       dashMatch[1][2] == "0" ||
      //       dashMatch[1][2] == "7" ||
      //       dashMatch[1][2] == "8" ||
      //       dashMatch[1][2] == "9"
      //     ) {
      //       return; // Block invalid character - Y !=  7, 8 or 9
      //     }
      //   }
      //   if (tickSize == ruleOfHalves) {
      //     if (
      //       dashMatch[1][2] == "0" ||
      //       dashMatch[1][2] == "1" ||
      //       dashMatch[1][2] == "6" ||
      //       dashMatch[1][2] == "7" ||
      //       dashMatch[1][2] == "8" ||
      //       dashMatch[1][2] == "9"
      //     ) {
      //       return; // Block invalid character - Y != 6, 7, 8 or 9
      //     }
      //   }
      //   if (tickSize == ruleOfWhole) {
      //     {
      //       return; // Block all
      //     }
      //   }
      // }
      // // Prevent the ruleOfWhole appecting a plus sign. It won't match on the dash match, so using the input.
      // if (tickSize == ruleOfWhole) {
      //   if (input.includes("+")) {
      //     return;
      //   }
    }
    // IF TICK SIZE IS NULL, 8 and 9 will be allowed to be entered.
    if (dashMatch && dashMatch[1].length > 3) {
      return; // Block invalid characters immediately
    }
    setValue(input);
  };

  // Ensures only valid values are saved
  const validateAndSave = () => {
    if (value == props.value) {
      return value;
    }
    if (isUsingDollarFractionLogic) {
      const params = { newValue: value };
      if (!regexCheckFractionQuote(params)) {
        setValue("");
        return "";
      }
      // Collapse any all-zero input ("0", "00", "0.00", "-0", "0-00", "-0-00", "00-000" …)
      // to the canonical "0-00" / "-0-00" form before running the fraction conversion.
      // Without this the `${-0}` coercion inside convertToDollarFraction drops the sign,
      // and forms like "000" survive verbatim into the store and the payload.
      let finalFormattedValue: string;
      if (isNumericallyZero(value)) {
        finalFormattedValue = normalizeZeroQuote(value, "$fr");
      } else {
        const reducedValue = truncateValueIfNeeded(value, notation);
        const formattedValue = convertToDollarFraction(reducedValue);
        finalFormattedValue = formatDollarFractionString(formattedValue);
      }

      if (isLegLevel) {
        // Update the object via referencing it into the generic function
        const paramsForStore = { data: {} };
        paramsForStore.data = updateFieldOnParamsToQuote(
          fullTradeData.esid,
          props,
          field,
          finalFormattedValue,
        );
        updateQuotesAcrossTables(paramsForStore);
      }
      // Returns may not be needed once value setters are used.
      return finalFormattedValue; // Return final value once running it through the formatter.
    }
    // Basic return for when notaiton is NOT $fr.
    // Collapse all-zero forms ("00", "000", "0.00", "-0" …) to canonical "0" / "-0"
    // so the payload and store stay consistent across notations.
    const updatedValue = isNumericallyZero(value)
      ? normalizeZeroQuote(value, notation)
      : truncateValueIfNeeded(value, notation).replace(/,/g, "");

    if (isLegLevel) {
      const paramsForStore = { data: {} };
      paramsForStore.data = updateFieldOnParamsToQuote(
        fullTradeData.esid,
        props,
        field,
        updatedValue,
      );
      updateQuotesAcrossTables(paramsForStore);
    }
    // Returns may not be needed once value setters are used.
    return updatedValue;
  };

  useImperativeHandle(ref, () => ({
    getValue: () => validateAndSave(),
  }));

  return (
    <input
      ref={inputRef}
      type={typeValue}
      value={
        value == props.value ? truncateValueIfNeeded(value, notation) : value
      }
      onChange={handleInput} // Blocks unwanted characters immediately
      className={`ag-input ag-roundedInputStyle`}
      // onBlur={handleBlur}
    />
  );
});

export default DynamicCellEditorQuoteDollarFraction;
